import type { NurseResponse } from "@/types/nurse-type";
import { sortNurseListByRoleThenSequence } from "@utils/common-util.ts";
import type * as type from "@vTypes";
import type { Version } from "@/hooks/roster-craete/useRosterVersions";
import type { CurDate, CurScheduleInfo } from "@/store/scheduleStore";
import type {
  InboundInfo,
  InboundItem,
} from "@/hooks/roster-craete/useRosterSchedule";
import type { RosterSchedule } from "@/hooks/roster-craete/useRosterSchedule";
import { inboundItemsFromIssuedAssignmentList } from "@/services/assignment";
import type { PreceptorUiAttachment } from "./preceptorUtils";

/** 스케줄 행 `assignments` — API가 스냅샷(snake/`transfer`) 형태로 줄 때 `InboundItem[]`로 맞춤 */
function normalizeAssignmentsOnScheduleRow<T extends object>(row: T): T {
  const list = "assignments" in row ? row.assignments : undefined;
  if (!Array.isArray(list) || list.length === 0) return row;
  const first = list[0] as Record<string, unknown> | undefined;
  if (first != null && typeof first.startDate === "string") {
    return { ...row, assignments: list as unknown as InboundItem[] };
  }
  const items = inboundItemsFromIssuedAssignmentList(list as unknown[]);
  if (items.length === 0) return row;
  return { ...row, assignments: items };
}

export function secondsToTimeString(seconds: number | string | null): string {
  if (seconds == null) return ""; // null, undefined만 처리
  const numSeconds = typeof seconds === "string" ? parseInt(seconds) : seconds;

  const hours = String(Math.floor(numSeconds / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((numSeconds % 3600) / 60)).padStart(2, "0");
  return `${hours}:${minutes}`;
}

export function makePreceptorGroupID(list: type.FullNurseSchema[]) {
  const preceptorMap = new Map<string, string[]>();

  list.forEach((nurse) => {
    const preceptorID = nurse.preceptor_id;
    const nurseID = nurse.nurse_id;
    if (preceptorID) {
      if (!preceptorMap.has(preceptorID)) {
        preceptorMap.set(preceptorID, []);
      }
      preceptorMap.get(preceptorID)!.push(nurseID);
    }
  });
  return Array.from(preceptorMap.entries()).map(([preceptor, preceptee]) => ({
    preceptor,
    preceptee,
  }));
}

/**
 * 간호사를 팀별로 그룹핑.
 *
 * `memberStatusMap`(근무자관리 `useGroupMembers` 의 `members[]` → `nurse_id→GroupMemberInMonth`)
 * 가 주어지면 **소속 팀을 `as_of_team`(시점 기준, 재분배·classify 미래 발효 포함)으로 결정**한다.
 * 그룹핑 규칙은 근무자관리 `NurseManagementTables` 와 동일:
 *   `member.as_of_team ?? nurse.team_id`
 *   (시점 팀(period) 우선, 없으면 캐시 `team_id` 폴백. N전담도 직접 배정한 팀을 표시)
 *
 * `memberStatusMap` 이 없으면(또는 비어 있으면) 기존 캐시(`team.team_members`) 기준으로 폴백한다.
 * 팀 이름·메타는 항상 `teamsSchema`(useTeamsQuery)에서 취하며, 멤버 배치만 시점 기준으로 바뀐다.
 */
export function filterTeam(
  teamsSchema: type.FetchTeams[],
  nurses: CopyNurses, // copyNurses
  memberStatusMap?: Map<
    string,
    { as_of_team: number | null; is_night_dedicated?: boolean }
  >
) {
  const useMonthMembership = !!memberStatusMap && memberStatusMap.size > 0;

  if (useMonthMembership) {
    // 시점(as_of_team) 기준 그룹핑 — 근무자관리와 동일 플로우.
    const knownTeamIds = new Set(
      teamsSchema.map((t) => t.team_id).filter((id): id is number => id != null)
    );
    const byTeamId = new Map<number, CopyNurses>();
    const unassigned: CopyNurses = [];

    for (const nurse of nurses) {
      const member = memberStatusMap!.get(nurse.nurse_id);
      // 시점 팀(as_of_team), 없으면 캐시 team_id 폴백. N전담도 직접 배정한 팀을 표시.
      const teamId = member?.as_of_team ?? nurse.team_id;
      // teamId 가 없거나 로드된 팀 메타에 없는 경우(메타 미존재) → 미등록으로 안전 배치(누락 방지).
      if (!teamId || !knownTeamIds.has(teamId)) {
        unassigned.push(nurse);
        continue;
      }
      const bucket = byTeamId.get(teamId);
      if (bucket) bucket.push(nurse);
      else byTeamId.set(teamId, [nurse]);
    }

    const matchedResult = teamsSchema.map((team) => ({
      teams: team,
      nurses: team.team_id != null ? (byTeamId.get(team.team_id) ?? []) : [],
    }));

    const unmatchedResult = {
      teams: { team_name: "미등록", team_id: null, team_members: [] },
      nurses: unassigned,
    };

    return [matchedResult, unmatchedResult];
  }

  // 폴백: 시점 정보가 없으면 기존 캐시(team_members) 기준 그룹핑 유지.
  const teamsFlatMap = teamsSchema.flatMap((m) => m.team_members);

  const matchedResult = teamsSchema.map((team) => {
    const findNurses = team.team_members.map((id) =>
      nurses.find((nurse) => nurse.nurse_id === id)
    );
    return { teams: team, nurses: findNurses };
  });

  const unmatchedResult = {
    teams: { team_name: "미등록", team_id: null, team_members: [] },
    nurses: nurses.filter((nurse) => !teamsFlatMap.includes(nurse.nurse_id)),
  };

  return [matchedResult, unmatchedResult];
}

type BasicRosterRow = { schedule: string[] };
type ShiftCountMap = Record<string, number>;

export function calculateStatistics(
  roster: BasicRosterRow[],
  daysInMonth: number
) {
  const nurseCounts: ShiftCountMap[] = roster.map(() => ({
    D: 0,
    E: 0,
    N: 0,
    O: 0,
  }));
  const dailyCounts = Array(daysInMonth)
    .fill(null)
    .map(() => ({ D: 0, E: 0, N: 0, O: 0 }) as ShiftCountMap);

  roster.forEach((nurse, nurseIndex) => {
    nurse.schedule.forEach((shift: string, dayIndex: number) => {
      // nurseCounts 계산
      if (nurseCounts[nurseIndex][shift] !== undefined) {
        nurseCounts[nurseIndex][shift]++;
      }
      // dailyCounts 계산
      if (dailyCounts[dayIndex] && dailyCounts[dayIndex][shift] !== undefined) {
        dailyCounts[dayIndex][shift]++;
      }
    });
  });

  return { nurseCounts, dailyCounts };
}

// ---------------------------------------------------------------------------
// pre_monthlyNurseSchedule 내부 타입
// ---------------------------------------------------------------------------

/** rawData 측(`nurses_json`) 간호사 프로필 */
type RawNurseProfile = {
  nurse_id: string | number;
  preceptor?: unknown;
  personal_off_adjustment?: number;
  /** level_ 로 내려오는 케이스 대비 */
  level_?: string | null;
  sequence?: number;
  active?: unknown;
  role?: string | null;
  grade?: number | null;
  age?: number | string | null;
  inbound?: InboundInfo | InboundItem[];
  is_inbound?: boolean;
};

/** targetList 측(`roster.nurses`) 스케줄 시트 행 */
type TargetScheduleEntry = {
  id?: string | number;
  nurse_id?: string | number;
  inbound?: InboundInfo | InboundItem[];
  is_inbound?: boolean;
};

type CopyNursePreceptor = null | {
  role: string;
  group: {
    preceptee: string[];
    preceptor: string;
  };
  color: string;
  or: string[];
  ee: string[];
};

export type CopyNurseRow = NurseResponse & {
  schedule: RosterSchedule["nurses"][number]["schedule"] | (string | null)[];
  schedule_ids?: RosterSchedule["nurses"][number]["schedule_ids"];
  counts: Record<string, number | null>;
  preceptor: CopyNursePreceptor;
};

export type FullNurseSchemaWithPreceptor = NurseResponse & {
  preceptor: PreceptorUiAttachment;
};

function copyNursePreceptor(
  nurse: NurseResponse | FullNurseSchemaWithPreceptor
): CopyNursePreceptor {
  return "preceptor" in nurse ? nurse.preceptor : null;
}

// ---------------------------------------------------------------------------
// Assignment 도메인 헬퍼
// ---------------------------------------------------------------------------

/** `{ inbound_list }` / 배열 모두 → `InboundItem[]` 정규화 */
function inboundItemsOf(
  v: InboundInfo | InboundItem[] | undefined
): InboundItem[] {
  if (v == null) return [];
  if (Array.isArray(v)) return v;
  return v.inbound_list ?? [];
}

/**
 * targetItems 우선, rawItems fallback — 동일 배정 중복 제거.
 * dedup 키: `startDate + target_group_id + reason`
 */
function dedupInboundItems(
  targetItems: InboundItem[],
  rawItems: InboundItem[]
): InboundItem[] {
  const keyOf = (x: InboundItem) =>
    `${x.startDate ?? ""}|${x.target_group_id ?? x.targetGroupId ?? ""}|${x.reason ?? ""}`;
  const seen = new Set<string>();
  const out: InboundItem[] = [];
  for (const item of [...targetItems, ...rawItems]) {
    const k = keyOf(item);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(item);
  }
  return out;
}

// ---------------------------------------------------------------------------

/**
 * 스케줄 데이터와 간호사 스키마를 병합하여 월간 간호사 스케줄 생성.
 *
 * 도메인별 머지 정책:
 * - **Profile** (preceptor / off / level / sequence / active / role / grade / age):
 *   rawData(`nurses_json`) 가 진실 소스 — 화이트리스트로 덮어씀
 * - **Schedule** (schedule / counts / 기타):
 *   targetList(`roster.nurses`) 의 `...rest` 로 보존
 * - **Assignment** (inbound / is_inbound):
 *   양쪽 union + dedup (targetList 우선). 위치에 무관하게 항상 보존.
 */
export function pre_monthlyNurseSchedule(
  rawData: FullNurseSchemaWithPreceptor[],
  targetList: RosterSchedule["nurses"]
): CopyNurseRow[];
export function pre_monthlyNurseSchedule(
  rawData: RawNurseProfile[],
  targetList: TargetScheduleEntry[]
): unknown[];
export function pre_monthlyNurseSchedule(
  rawData: RawNurseProfile[],
  targetList: TargetScheduleEntry[]
): unknown[] {
  // nurse_id → 프로필 매핑 (숫자/문자 혼재 대비 키 통일)
  const profileMap = new Map<string, RawNurseProfile>(
    rawData.map((n) => [String(n.nurse_id), n])
  );

  const mergedList = targetList.map((entry) => {
    const id = String(entry.id ?? entry.nurse_id ?? "");
    const { inbound: tInbound, is_inbound: tIsInbound, ...restWithIds } = entry;
    const rest = { ...restWithIds };
    delete rest.id;
    delete rest.nurse_id;

    const profile = id !== "" ? profileMap.get(id) : undefined;
    const profileBase = profile ?? {};

    // Assignment: targetList 보유 항목 우선, rawData 항목으로 보완
    const inboundUnion = dedupInboundItems(
      inboundItemsOf(tInbound),
      inboundItemsOf(profile?.inbound)
    );

    return {
      ...profileBase,
      // Schedule 도메인 — targetList ...rest 보존
      ...rest,
      nurse_id: id !== "" ? id : undefined,
      // Profile 도메인 — rawData 화이트리스트
      preceptor: profile?.preceptor ?? null,
      off: profile?.personal_off_adjustment ?? 0,
      level: profile?.level_ ?? null,
      sequence: profile?.sequence ?? 0,
      active: profile?.active,
      role: profile?.role ?? null,
      grade: profile?.grade ?? null,
      age: profile?.age ?? null,
      // Assignment 도메인 — union + dedup
      inbound: inboundUnion,
      is_inbound: tIsInbound ?? profile?.is_inbound ?? inboundUnion.length > 0,
    };
  });

  return sortNurseListByRoleThenSequence(mergedList);
}

/**
 * 스케줄 데이터와 간호사 스키마를 기반으로 copyNurses 계산
 */
export function computeCopyNurses({
  scheduleData,
  fullNurseSchema,
  fullNurseSchemaAddPreceptor,
  curDate,
}: {
  scheduleData: RosterSchedule | null;
  fullNurseSchema: NurseResponse[];
  fullNurseSchemaAddPreceptor?: FullNurseSchemaWithPreceptor[];
  curDate: CurDate | null;
}): CopyNurseRow[] {
  // 스케줄 데이터가 있으면 병합하여 반환
  if (scheduleData?.nurses && fullNurseSchemaAddPreceptor) {
    const monthlySchedule = pre_monthlyNurseSchedule(
      fullNurseSchemaAddPreceptor,
      scheduleData.nurses
    );

    // active인원만 + 병동이동·파견 등 `assignments`를 InboundItem[]로 정규화(만들기 표 기간 바)
    return monthlySchedule
      .filter((e) => e.active)
      .map((row) => normalizeAssignmentsOnScheduleRow(row));
  }

  // 스케줄 데이터가 없으면 빈 스케줄로 초기화
  if (fullNurseSchema && fullNurseSchema.length > 0) {
    const daysInMonth = new Date(
      curDate?.year ?? new Date().getFullYear(),
      curDate?.month ?? new Date().getMonth() + 1,
      0
    ).getDate();
    const baseNurses =
      fullNurseSchemaAddPreceptor && fullNurseSchemaAddPreceptor.length > 0
        ? fullNurseSchemaAddPreceptor
        : fullNurseSchema;

    const rows: CopyNurseRow[] = baseNurses
      .filter((nurse) => nurse.active)
      .map((nurse) => ({
        ...nurse,
        schedule: Array(daysInMonth).fill("-"),
        counts: { D: 0, E: 0, N: 0, O: 0 },
        preceptor: copyNursePreceptor(nurse),
      }));

    return sortNurseListByRoleThenSequence(rows);
  }

  return [];
}

/** `computeCopyNurses` 결과 — 근무표 편집용 간호사+스케줄 행 */
export type CopyNurses = ReturnType<typeof computeCopyNurses>;

/**
 * 버전 리스트에서 최신 스케줄 ID 선택
 * @param list 버전 리스트
 * @param target "issued" - 마감된 버전 중 최신, "current" - 첫 번째 버전
 * @returns 선택된 스케줄 정보 또는 null
 */

export function SelectVersionHandler(
  list: Version[],
  target: "issued" | "current"
): CurScheduleInfo | null {
  // 배열인지 확인
  if (Array.isArray(list) && list.length > 0) {
    let scheduleVer: number, scheduleID: string;
    if (target === "issued") {
      // issued 상태인 버전들 중에서 버전 번호가 가장 높은 것 선택
      const issuedVersions = list.filter((item) => item.status === "issued");
      if (issuedVersions.length > 0) {
        // 버전 번호 기준으로 내림차순 정렬하여 최신 버전 선택
        const latestIssued = [...issuedVersions].sort(
          (a, b) => b.version - a.version
        )[0];
        scheduleVer = latestIssued.version;
        scheduleID = latestIssued.schedule_id;
      } else {
        // issued가 없으면 전체 리스트에서 버전 번호가 가장 높은 것 선택
        const latestVersion = [...list].sort(
          (a, b) => b.version - a.version
        )[0];
        scheduleVer = latestVersion.version;
        scheduleID = latestVersion.schedule_id;
      }
    } else {
      const latest = list[0];
      scheduleVer = latest.version;
      scheduleID = latest.schedule_id;
    }
    const selected =
      target === "current"
        ? list[0]
        : list.find((item) => item.schedule_id === scheduleID);
    return {
      scheduleId: scheduleID,
      version: scheduleVer,
      status: selected?.status ?? list[0].status,
    };
  }
  return null;
}
